import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { OcAvatar } from "oc/components";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import parsePipedName from "oc/utils/get-parse-pipedName";

interface MyNewsListItemHeaderProps {
  item: {
    title?: string;
    editorName?: string;
    editorEmployeeCode?: string;
    publishedAt?: string;
    createdAt?: string;
    validTo?: string;
    modifiedAt?: string;
  };
}

export const MyNewsListItemHeader: React.FC<MyNewsListItemHeaderProps> = ({
  item,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useOcIsMobile();

  const parsedName = parsePipedName(item?.editorName ?? "", i18n);
  const itemDate = item?.publishedAt ? item?.publishedAt : item?.createdAt;

  type StatusKey = "unpublished" | "expired" | "default";
  const statusConfig: Record<
    StatusKey,
    { backgroundColor: string; color: string; infoText: string }
  > = {
    unpublished: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.white.main,
      infoText: t("MY_NEWS_UNPUBLISHED_NEWS"),
    },
    expired: {
      backgroundColor: theme.palette.gray80.main,
      color: theme.palette.white.main,
      infoText: t("MY_NEWS_ARCHIVED_NEWS"),
    },
    default: {
      backgroundColor: theme.palette.white.main,
      color: theme.palette.gray80.main,
      infoText: "",
    },
  };

  let currentStatus: StatusKey = "default";
  if (!item?.publishedAt) {
    currentStatus = "unpublished";
  } else if (
    item?.validTo &&
    moment(item?.validTo).isBefore(moment(), "minute")
  ) {
    currentStatus = "expired";
  }

  const { backgroundColor, color, infoText } = statusConfig[currentStatus];

  const hideDateAndModified =
    currentStatus === "unpublished" || currentStatus === "expired";

  return (
    <Box
      id="news-header"
      display="flex"
      flexDirection="row"
      alignItems={isMobile ? "flex-start" : "center"}
      justifyContent="space-between"
      padding="16px"
      style={{
        backgroundColor,
        borderRadius: "0px 0px 8px 8px",
      }}
    >
      <Box display="flex" flexDirection="row" alignItems="center">
        <OcAvatar
          employee={item?.editorEmployeeCode ?? ""}
          style={{
            width: "45px",
            height: "45px",
          }}
        />
        <Box width="16px" />
        <Box>
          <Typography
            variant="h6"
            style={{
              color,
            }}
          >
            {item?.title}
          </Typography>
          <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
            <Typography
              variant="body2"
              style={{
                color,
              }}
            >
              {parsedName}
            </Typography>
            {!hideDateAndModified && (
              <>
                <Box width="16px" />
                <Typography
                  variant="body2"
                  style={{
                    color,
                  }}
                >
                  {moment(itemDate)
                    .format("MMMM DD., HH:mm")
                    .replace(/^\w/, (c) => c.toUpperCase())}
                </Typography>
                {item?.modifiedAt && (
                  <>
                    <Box width="16px" />
                    <Typography
                      variant="body2"
                      style={{
                        color: theme.palette.owGray.main,
                        position: "relative",
                        display: "inline-block",
                        paddingLeft: "10px",
                      }}
                    >
                      <span
                        style={{
                          content: '""',
                          position: "absolute",
                          left: "0",
                          top: "50%",
                          width: "4px",
                          height: "4px",
                          backgroundColor: theme.palette.owGray.main,
                          borderRadius: "50%",
                          transform: "translateY(-50%)",
                        }}
                      />
                      {t("MY_NEWS_EDITED")}
                    </Typography>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      {isMobile && <Box height="32px" />}
      {infoText && (
        <Typography
          variant="h6"
          style={{
            color: theme.palette.white.main,
          }}
        >
          {infoText}
        </Typography>
      )}
    </Box>
  );
};

export default MyNewsListItemHeader;
